import { UseQueryResult, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Dispatch, SetStateAction, useState, useMemo } from 'react';
import { UserData } from '@cbo/shared-library/types/bslCore/provisioning';
import useLaborRequests from '.';
import staleTimeSettings from '../../config/reactQueryConfig';
import { PaginationResponse } from '../../models/Paginated';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import SalesUtilities from '../../../sales/utilities';
import {
  EmployeeBreakReportCommonRequestSchema,
  GenericPayrollExportGridDataResponse,
  IntervalSalesAndLaborReportCommonRequestSchema,
  JobcodeHistoryGridRequest,
  JobcodeHistoryGridResponse,
  OvertimeThresholdCommonRequest,
  OvertimeThresholdGridDataResponse,
  OvertimeThresholdKpiCardsResponse,
  EmployeeSalesPerformanceCommonRequest,
  EmployeeSalesPerformanceKpiCardsResponse,
  EmployeeSalesPerformanceGridDataResponse,
  ApproachingACAHourlyThresholdGridDataRequest,
  ApproachingACAHourlyThresholdGridDataResponse,
  ApproachingACAThresholdAverageHoursWorkedGridResponse,
  EmployeePayrollDto,
  Job,
  LaborGroup,
  JobProfile,
  EmployeeDetailsInfo,
  Employee,
  GetEmployeesRequestSchema,
  GetPaginatedJobsRequest,
  GetJobProfilesRequestSchema,
  SelfJobProfile,
  EmployeeSitesGridRow,
  GetTipDistributionResponseDto,
  BannerStateViewDto,
  BannerType,
  BannerEntityType,
} from '../types';
import { TipDistributionConfigResponseDto, ContributionPoolTotalsByShiftResponse } from '../TipShareDistribution/types';
import useLaborQueryKeys, { useBspQueryKeys } from './queryKeys';
import { BaseReportDataRequest } from '../../../api/sales/requests';

export const useGetEmployeeSalesPerformanceReportGridData = () => {
  const { getEmployeeSalesPerformanceReportGridData } = useLaborRequests();
  const [request, setEmployeeSalesPerformanceReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeeSalesPerformanceCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeeSalesPerformanceReport.list(request, 'grid-data'),
    () => getEmployeeSalesPerformanceReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    employeeSalesPerformanceReportGridDataResponse: data as EmployeeSalesPerformanceGridDataResponse | undefined,
    setEmployeeSalesPerformanceReportGridDataRequest,
    isEmployeeSalesPerformanceReportGridDataLoading: isLoading,
  };
};

export const useGetEmployeeSalesPerformanceReportKpiCardsData = () => {
  const { getEmployeeSalesPerformanceReportKpiCardsData } = useLaborRequests();
  const [request, setEmployeeSalesPerformanceReportKpiCardsDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeeSalesPerformanceCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeeSalesPerformanceReport.list(request, 'kpi-cards'),
    () => getEmployeeSalesPerformanceReportKpiCardsData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    employeeSalesPerformanceReportKpiCardsDataResponse: data as EmployeeSalesPerformanceKpiCardsResponse | undefined,
    setEmployeeSalesPerformanceReportKpiCardsDataRequest,
    isEmployeeSalesPerformanceReportKpiCardsDataLoading: isLoading,
  };
};

export const useGetOvertimeThresholdReportGridData = () => {
  const { getOvertimeThresholdReportGridData } = useLaborRequests();
  const [request, setOvertimeThresholdReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as OvertimeThresholdCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().overtimeThresholdReport.list(request, 'grid-data'),
    () => getOvertimeThresholdReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    overtimeThresholdReportGridDataResponse: data as OvertimeThresholdGridDataResponse | undefined,
    setOvertimeThresholdReportGridDataRequest,
    isOvertimeThresholdReportGridDataLoading: isLoading,
  };
};

export const useGetOvertimeThresholdReportKpiCardsData = () => {
  const { getOvertimeThresholdReportKpiCardsData } = useLaborRequests();
  const [request, setOvertimeThresholdReportKpiCardsDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as OvertimeThresholdCommonRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().overtimeThresholdReport.list(request, 'kpi-cards'),
    () => getOvertimeThresholdReportKpiCardsData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    overtimeThresholdReportKpiCardsDataResponse: data as OvertimeThresholdKpiCardsResponse | undefined,
    setOvertimeThresholdReportKpiCardsDataRequest,
    isOvertimeThresholdReportKpiCardsDataLoading: isLoading,
  };
};

export const useEmployeeSelfQuery = (isAuthenticated: boolean): UseQueryResult<Employee> => {
  const { getSelfEmployee } = useLaborRequests();
  return useQuery(useLaborQueryKeys().employee.listByIdSelf(), () => getSelfEmployee(), {
    staleTime: staleTimeSettings.NONE,
    useErrorBoundary: false,
    enabled: isAuthenticated,
  });
};

export const useEmployeeDetailsSelfQuery = (isAuthenticated: boolean): UseQueryResult<EmployeeDetailsInfo> => {
  const { getSelfEmployeeDetailsInfo } = useLaborRequests();

  return useQuery(useLaborQueryKeys().employeeDetails.self(), () => getSelfEmployeeDetailsInfo(), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
    enabled: isAuthenticated,
  });
};

export const useJobProfileSelfQuery = (isAuthenticated: boolean): UseQueryResult<SelfJobProfile[]> => {
  const { getSelfJobProfile } = useLaborRequests();

  return useQuery(useLaborQueryKeys().jobProfile.self(), () => getSelfJobProfile(), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: false,
    enabled: isAuthenticated,
  });
};

export const useJobcodeHistoryQuery = (
  request: JobcodeHistoryGridRequest
): UseQueryResult<JobcodeHistoryGridResponse> => {
  const { getJobcodeHistoryGridData } = useLaborRequests();

  return useQuery(useLaborQueryKeys().jobcodeHistory.list(request), () => getJobcodeHistoryGridData(request), {
    staleTime: staleTimeSettings.NONE,
    useErrorBoundary: true,
    keepPreviousData: true,
  });
};

// TODO - Rename once all pages are updated to use new API's
export const useLaborGroupsQuery = (): UseQueryResult<PaginationResponse<LaborGroup>> => {
  const { getLaborGroups } = useLaborRequests();

  return useQuery(useLaborQueryKeys().laborGroup.all, () => getLaborGroups());
};

// TODO : Rename once all pages are updated to use new API's
export const useLaborGroupByIdQuery = (laborGroupId: string): UseQueryResult<LaborGroup> => {
  const { getLaborGroupByIdApi } = useLaborRequests();
  return useQuery(useLaborQueryKeys().laborGroup.listById(laborGroupId), () => getLaborGroupByIdApi(laborGroupId), {
    enabled: laborGroupId.length > 0,
    staleTime: staleTimeSettings.LOW,
    useErrorBoundary: true,
  });
};

export const useGetEmployeeTipReportGridDataQuery = () => {
  const { getEmployeeTipReportGridData } = useLaborRequests();
  const [request, setGetEmployeeTipReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeeBreakReportCommonRequestSchema);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeeTipReport.page('grid-data', request),
    () => getEmployeeTipReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: true,
      enabled: isEnabled,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    employeeTipReportGridData: data,
    setGetEmployeeTipReportGridDataRequest,
    isGetEmployeeTipReportGridDataLoading: isLoading,
  };
};

export const useGetEmployeeTipReportKpiCardsAndChartsDataQuery = () => {
  const { getEmployeeTipReportKpiCardsData } = useLaborRequests();
  const [request, setGetEmployeeTipReportKpiCardsDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeeBreakReportCommonRequestSchema);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeeTipReport.page('kpi-cards', request),
    () => getEmployeeTipReportKpiCardsData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    employeeTipReportKpiCardsData: data,
    setGetEmployeeTipReportKpiCardsDataRequest,
    isGetEmployeeTipReportKpiCardsDataLoading: isLoading,
  };
};

export const useGetEmployeeBreakReportGridDataQuery = () => {
  const { getEmployeeBreakReportGridData } = useLaborRequests();
  const [request, setGetEmployeeBreakReportGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeeBreakReportCommonRequestSchema);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeeBreakReport.list('grid-data', request),
    () => getEmployeeBreakReportGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    employeeBreakReportGridData: data,
    setGetEmployeeBreakReportGridDataRequest,
    isGetEmployeeBreakReportGridDataLoading: isLoading,
  };
};

export const useGetEmployeeBreakReportKpiCardsDataQuery = () => {
  const { getEmployeeBreakReportKpiCardsData } = useLaborRequests();
  const [request, setGetEmployeeBreakReportKpiCardsDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeeBreakReportCommonRequestSchema);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeeBreakReport.list('kpi-cards', request),
    () => getEmployeeBreakReportKpiCardsData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    employeeBreakReportKpiCardsData: data,
    setGetEmployeeBreakReportKpiCardsDataRequest,
    isGetEmployeeBreakReportKpiCardsDataLoading: isLoading,
  };
};

export const useGetIntervalSalesAndLaborReportDataQuery = () => {
  const { getIntervalSalesAndLabor } = useLaborRequests();
  const [request, setGetIntervalSalesAndLaborReportDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as IntervalSalesAndLaborReportCommonRequestSchema);

  /* istanbul ignore next */
  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().intervalSalesAndLaborReport.list(request),
    () => getIntervalSalesAndLabor(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    intervalSalesAndLaborReportData: data,
    setGetIntervalSalesAndLaborReportDataRequest,
    isIntervalSalesAndLaborReportDataLoading: isLoading,
  };
};

export const useGetGenericPayrollExportGridData = () => {
  const { getGenericPayrollExportGridData } = useLaborRequests();
  const [request, setGenericPayrollGridData] = useState({
    startDate: '',
    endDate: '',
  } as BaseReportDataRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().genericPayrollExport.grid(request),
    () => getGenericPayrollExportGridData(request),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    genericPayrollData: data as GenericPayrollExportGridDataResponse[] | undefined,
    setGenericPayrollGridData,
    isLoading,
  };
};

export const useGetApproachingACAHourlyThresholdGridData = () => {
  const { getApproachingACAHourlyThresholdGridData } = useLaborRequests();
  const [request, setGetApproachingACAHourlyThresholdGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as ApproachingACAHourlyThresholdGridDataRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().acaThresholdReport.hourlyGrid(request),
    () => getApproachingACAHourlyThresholdGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    getApproachingACAHourlyThresholdGridData: data as ApproachingACAHourlyThresholdGridDataResponse | undefined,
    setGetApproachingACAHourlyThresholdGridDataRequest,
    isGetApproachingACAHourlyThresholdGridDataLoading: isLoading,
  };
};

export const useGetAcaAverageHoursGridData = () => {
  const { getAcaAverageHoursGridData } = useLaborRequests();
  const [request, setACAAverageHoursRequest] = useState({
    startDate: '',
    endDate: '',
  } as BaseReportDataRequest);

  const isEnabled =
    request.startDate.length > 0 &&
    request.endDate.length > 0 &&
    request.startDate !== 'Invalid Date' &&
    request.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().acaThresholdReport.averageHoursGrid(request),
    () => getAcaAverageHoursGridData(request),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: true,
    }
  );

  return {
    gridData: data as ApproachingACAThresholdAverageHoursWorkedGridResponse | undefined,
    setACAAverageHoursRequest,
    isLoading,
  };
};

export const useEmployeePayrollGridQuery = () => {
  const { getEmployeePayrollGridData } = useLaborRequests();
  const [payrollGridRequest, setPayrollGridRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeePayrollDto);
  const isEnabled =
    payrollGridRequest.startDate.length > 0 &&
    payrollGridRequest.endDate.length > 0 &&
    payrollGridRequest.startDate !== 'Invalid Date' &&
    payrollGridRequest.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeePayrollSummary.grid(payrollGridRequest, 'summary'),
    () => getEmployeePayrollGridData(payrollGridRequest),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    data,
    setPayrollGridRequest,
    loading: isLoading,
  };
};

export const useEmployeePayrollKPICardsQuery = () => {
  const { getEmployeePayrollKPICardsData } = useLaborRequests();
  const [payrollKpiRequest, setKPICardsRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeePayrollDto);
  const isEnabled =
    payrollKpiRequest.startDate.length > 0 &&
    payrollKpiRequest.endDate.length > 0 &&
    payrollKpiRequest.startDate !== 'Invalid Date' &&
    payrollKpiRequest.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeePayrollSummary.list(payrollKpiRequest, 'kpi-cards'),
    () => getEmployeePayrollKPICardsData(payrollKpiRequest),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    data,
    setKPICardsRequest,
    loading: isLoading,
  };
};

export const useGetEmployeePayrollDetailGridData = () => {
  const { getEmployeePayrollDetailGridData } = useLaborRequests();

  const [payrollDetailGridRequest, setEmployeePayrollDetailGridDataRequest] = useState({
    startDate: '',
    endDate: '',
  } as EmployeePayrollDto);

  const isEnabled =
    payrollDetailGridRequest.startDate.length > 0 &&
    payrollDetailGridRequest.endDate.length > 0 &&
    payrollDetailGridRequest.startDate !== 'Invalid Date' &&
    payrollDetailGridRequest.endDate !== 'Invalid Date';

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().employeePayrollSummary.grid(payrollDetailGridRequest, 'detail'),
    () => getEmployeePayrollDetailGridData(payrollDetailGridRequest),
    {
      staleTime: 2 * 60 * 1000, // custom stale to account for NCRA update interval
      enabled: isEnabled,
      useErrorBoundary: false,
      retry: SalesUtilities.shouldRetry,
    }
  );

  return {
    employeePayrollDetailGridDataResponse: data,
    setEmployeePayrollDetailGridDataRequest,
    isEmployeePayrollDetailDataGridLoading: isLoading,
  };
};

export const useJobsQuery = (
  isAuthenticated: boolean,
  query?: GetPaginatedJobsRequest
): UseQueryResult<PaginationResponse<Job>> => {
  const { getJobs } = useLaborRequests();
  return useQuery(useLaborQueryKeys().jobcode.list(query), () => getJobs(query), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: true,
    keepPreviousData: true,
    enabled: isAuthenticated,
    refetchOnWindowFocus: false,
  });
};

export const useJobsByListIdsQuery = (
  jobIds: string[],
  isAuthenticated: boolean
): UseQueryResult<PaginationResponse<Job>> => {
  const { getJobs } = useLaborRequests();

  return useQuery(useLaborQueryKeys().jobcode.jobListByIds(jobIds), () => getJobs({ ids: jobIds }), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: true,
    keepPreviousData: true,
    enabled: isAuthenticated,
  });
};

export const useJobByIdQuery = (jobId: string, isAuthenticated: boolean): UseQueryResult<Job> => {
  const { getJobById } = useLaborRequests();

  return useQuery(useLaborQueryKeys().jobcode.byId(jobId), () => getJobById(jobId), {
    staleTime: staleTimeSettings.LOW,
    useErrorBoundary: true,
    enabled: isAuthenticated && jobId.length > 0,
    refetchOnWindowFocus: false,
  });
};

export const useGetEmployeesQuery = (
  authenticated: boolean,
  params: GetEmployeesRequestSchema,
  enabled: boolean,
  gettingByPosEmployeeId?: boolean,
  excludeEUID?: boolean
) => {
  const { getEmployees } = useLaborRequests();
  const { siteId } = useCurrentSite();

  // Memoize the final params to prevent unnecessary rerenders
  const queryParams = useMemo(
    () => (excludeEUID ? { ...params } : { ...params, enterpriseUnit: siteId }),
    [params, siteId, excludeEUID]
  );

  // Calculate if query should be enabled
  const isEnabled = useMemo(() => {
    if (!authenticated || !enabled) return false;

    if (gettingByPosEmployeeId) {
      return (params.posEmployeeIds?.length ?? 0) > 0;
    }

    return true;
  }, [authenticated, enabled, gettingByPosEmployeeId, params.posEmployeeIds]);

  const { data, refetch, isLoading } = useQuery(
    useLaborQueryKeys().employee.listByFilters(queryParams),
    () => getEmployees(queryParams, excludeEUID),
    {
      staleTime: gettingByPosEmployeeId ? staleTimeSettings.NONE : staleTimeSettings.MEDIUM,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled: isEnabled,
    }
  );

  return { data, isLoading, refetch };
};

export const useGetAsmEmployeesQuery = (
  authenticated: boolean,
  params: GetEmployeesRequestSchema,
  enabled: boolean
) => {
  const { getAsmEmployees } = useLaborRequests();
  const { siteId } = useCurrentSite();
  const [requestParameters, setRequestParameters] = useState<GetEmployeesRequestSchema>(params);

  const { data, refetch, isLoading } = useQuery(
    useLaborQueryKeys().employee.listByFilters({ ...requestParameters, enterpriseUnit: siteId }),
    () => getAsmEmployees(requestParameters),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled: authenticated && enabled,
    }
  );
  return { requestParameters, setRequestParameters, data, isLoading, refetch };
};

export const useGetJobProfilesByJobIdQuery = (jobId: string): UseQueryResult<PaginationResponse<JobProfile>> => {
  const { getJobProfilesByJobId } = useLaborRequests();

  return useQuery(useLaborQueryKeys().jobProfile.listById(jobId), () => getJobProfilesByJobId(jobId), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: true,
    enabled: jobId.length > 0,
  });
};

export const useGetEmployeeByEmployeeIdQuery = (
  employeeId: string,
  isAuthenticated: boolean,
  querySiteId?: string | null,
  fullEmploymentHistory?: boolean,
  isCfcOrg?: boolean
): UseQueryResult<Employee> => {
  const { getEmployeeByEmployeeId } = useLaborRequests();

  return useQuery(
    [...useLaborQueryKeys().employee.byEmpId(employeeId), querySiteId],
    () => getEmployeeByEmployeeId(employeeId, querySiteId, fullEmploymentHistory, isCfcOrg),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
      enabled: isAuthenticated && employeeId.length > 0,
    }
  );
};

export const useGetJobProfilesByEmployeeIdQuery = (
  empId: string,
  isAuthenticated: boolean,
  includeHistory = false
): UseQueryResult<PaginationResponse<JobProfile>> => {
  const { getJobProfilesByEmployeeId } = useLaborRequests();

  return useQuery(
    useLaborQueryKeys().jobProfile.jobProfilesByEmployeeId(empId),
    () => getJobProfilesByEmployeeId(empId, includeHistory),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
      enabled: isAuthenticated && empId.length > 0,
    }
  );
};

export const useGetEmployeeSitesByEmployeeIdQuery = (
  empId: string,
  isAuthenticated: boolean
): UseQueryResult<PaginationResponse<EmployeeSitesGridRow>> => {
  const { getEmployeeSitesByEmployeeId } = useLaborRequests();

  return useQuery(
    useLaborQueryKeys().employeeSites.employeeSitesByEmployeeId(empId),
    () => getEmployeeSitesByEmployeeId(empId),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
      enabled: isAuthenticated && empId.length > 0,
    }
  );
};

export const useGetUrlWithEmployeeIdQuery = (): {
  setEmployeeId: Dispatch<SetStateAction<string>>;
  url: string | undefined;
  setEnabled: Dispatch<SetStateAction<boolean>>;
  setQuerySiteId: (siteId: string) => void;
} => {
  const { getUrlWithEmployeeId } = useLaborRequests();
  const [employeeId, setEmployeeId] = useState('');
  const [enabled, setEnabled] = useState<boolean>(true);
  const [querySiteId, setQuerySiteId] = useState<string>('');

  const { data: url } = useQuery(
    ['employees', employeeId, querySiteId],
    async (): Promise<string | undefined> => getUrlWithEmployeeId(employeeId, querySiteId),
    {
      staleTime: staleTimeSettings.HIGH,
      enabled: enabled && employeeId?.length > 0,
      useErrorBoundary: false,
    }
  );

  return {
    url,
    setEmployeeId,
    setEnabled,
    setQuerySiteId,
  };
};

export const useGetAllJobProfilesQuery = (isAuthenticated: boolean, params?: GetJobProfilesRequestSchema) => {
  const { getAllJobProfiles } = useLaborRequests();

  const { data, refetch, isLoading, hasNextPage, fetchNextPage } = useInfiniteQuery(
    useLaborQueryKeys().jobProfile.all,
    ({ pageParam }) => getAllJobProfiles(pageParam ?? params),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: true,
      enabled: isAuthenticated,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        lastPage?.lastPage ? undefined : { pagenum: lastPage!.pageNumber + 1, ...params },
    }
  );

  return {
    data,
    isLoading,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
};

export const useGetAllJobsQuery = (isAuthenticated: boolean, query?: GetPaginatedJobsRequest) => {
  const { getJobs } = useLaborRequests();
  const { data, refetch, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    useLaborQueryKeys().jobcode.allJobs(query),
    ({ pageParam }) => getJobs({ ...query, ...pageParam }),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: true,
      keepPreviousData: true,
      enabled: isAuthenticated,
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage) =>
        lastPage?.lastPage ? undefined : { ...query, pagenum: (lastPage?.pageNumber ?? 0) + 1 },
    }
  );

  return {
    data,
    isLoading,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};

export const useGetBspUser = (isUserAuth: boolean, ncrUserId: string): UseQueryResult<UserData | undefined> => {
  const { getBspUser } = useLaborRequests();

  return useQuery(useBspQueryKeys().provisioning.userByUserId(ncrUserId), () => getBspUser(ncrUserId), {
    enabled: isUserAuth && Boolean(ncrUserId),
    staleTime: staleTimeSettings.MEDIUM,
    cacheTime: staleTimeSettings.HIGH,
    useErrorBoundary: true,
  });
};

export const useGetMultipleBspUsers = (
  isUserAuth: boolean,
  ncrUserIds: string[]
): UseQueryResult<(void | UserData)[], unknown> => {
  const { getBspUser } = useLaborRequests();
  return useQuery(
    useBspQueryKeys().provisioning.usersByIds(ncrUserIds),
    () => Promise.all(ncrUserIds.map((userId) => getBspUser(userId))),
    {
      enabled: isUserAuth && ncrUserIds.length > 0,
      staleTime: staleTimeSettings.MEDIUM,
      cacheTime: staleTimeSettings.HIGH,
      useErrorBoundary: true,
    }
  );
};

export const useGetTipDistribution = (
  businessDate: string | null
): UseQueryResult<GetTipDistributionResponseDto | undefined> => {
  const { getTipDistribution } = useLaborRequests();
  const laborQueryKeys = useLaborQueryKeys();

  return useQuery(
    laborQueryKeys.tipDistribution.byDate(businessDate ?? ''),
    () => getTipDistribution(businessDate ?? ''),
    {
      enabled: !!businessDate,
      staleTime: staleTimeSettings.MEDIUM,
      cacheTime: staleTimeSettings.HIGH,
      useErrorBoundary: true,
    }
  );
};

export const useGetTipConfiguration = (
  enabled = true
): UseQueryResult<TipDistributionConfigResponseDto[] | undefined> => {
  const { getTipConfiguration } = useLaborRequests();
  const laborQueryKeys = useLaborQueryKeys();

  return useQuery(laborQueryKeys.tipConfiguration.all, () => getTipConfiguration(), {
    staleTime: staleTimeSettings.HIGH,
    cacheTime: staleTimeSettings.HIGH,
    useErrorBoundary: true,
    enabled,
  });
};

export const useGetTipShareDistributionConfig = (enabled = true) => {
  const { getTipShareDistributionConfig } = useLaborRequests();

  const { data, isLoading, error } = useQuery(
    useLaborQueryKeys().tipShare.distribution(),
    () => getTipShareDistributionConfig(),
    {
      staleTime: staleTimeSettings.HIGH,
      useErrorBoundary: false,
      retry: false,
      enabled,
    }
  );

  return {
    distributionConfig: data,
    isDistributionLoading: isLoading,
    distributionError: error,
  };
};

export const useGetTipShareContributionConfig = () => {
  const { getTipShareContributionConfig } = useLaborRequests();

  const { data, isLoading, error } = useQuery(
    useLaborQueryKeys().tipShare.contribution(),
    () => getTipShareContributionConfig(),
    {
      staleTime: staleTimeSettings.HIGH,
      useErrorBoundary: false,
      retry: false,
    }
  );

  return {
    contributionConfig: data,
    isContributionLoading: isLoading,
    contributionError: error,
  };
};

export const useGetContributionPoolTotals = (businessDate: string | null, enabled = true) => {
  const { getContributionPoolTotals } = useLaborRequests();

  const { data, isLoading } = useQuery(
    useLaborQueryKeys().tipShare.contributionPoolTotals(businessDate ?? ''),
    () => getContributionPoolTotals(businessDate ?? ''),
    {
      staleTime: staleTimeSettings.MEDIUM,
      enabled: enabled && !!businessDate,
      useErrorBoundary: false,
      retry: false,
    }
  );

  return {
    poolTotals: data as ContributionPoolTotalsByShiftResponse | undefined,
    isPoolTotalsLoading: isLoading,
  };
};

export const useGetTipShareShifts = (enabled = true) => {
  const { listTipShareShifts } = useLaborRequests();
  const laborQueryKeys = useLaborQueryKeys();

  return useQuery(laborQueryKeys.tipShareShift.all, () => listTipShareShifts(), {
    staleTime: staleTimeSettings.MEDIUM,
    useErrorBoundary: true,
    enabled,
  });
};

export const useGetTipShareShift = (shiftId: string) => {
  const { getTipShareShift } = useLaborRequests();
  const laborQueryKeys = useLaborQueryKeys();

  return useQuery(laborQueryKeys.tipShareShift.detail(shiftId), () => getTipShareShift(shiftId), {
    staleTime: staleTimeSettings.MEDIUM,
    enabled: !!shiftId,
    useErrorBoundary: true,
  });
};

export const useBannerStateQuery = (
  employeeId: string,
  bannerType: BannerType,
  isAuthenticated: boolean
): UseQueryResult<BannerStateViewDto> => {
  const { getBannerState } = useLaborRequests();
  const laborQueryKeys = useLaborQueryKeys();

  return useQuery(
    laborQueryKeys.bannerState.byEmployeeAndType(employeeId, bannerType),
    () =>
      getBannerState({
        entityType: BannerEntityType.EMPLOYEE,
        entityId: employeeId,
        bannerType,
      }),
    {
      staleTime: staleTimeSettings.MEDIUM,
      useErrorBoundary: false,
      enabled: isAuthenticated && employeeId.length > 0,
      retry: false,
    }
  );
};
